// src/hooks/licenseApi.ts
import axios, { AxiosRequestConfig } from 'axios';

const BASE_URL = process.env.REACT_APP_URL_LICENSE;
const TOKEN_KEY = 'license-auth-token';

// credenciais em memória (não persistem)
let creds: { email: string; senha: string } | null = null;
export function setLicenseCredentials(email: string, senha: string) {
  creds = { email, senha };
}
export function clearLicenseCredentials() {
  creds = null;
}

// ---- utils JWT
function decodeSegment(seg: string) {
  try {
    return JSON.parse(
      decodeURIComponent(
        atob(seg.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    );
  } catch {
    return null;
  }
}
function getJwtExp(token?: string): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = decodeSegment(parts[1]);
  return payload?.exp ? Number(payload.exp) : null;
}
function isExpired(token?: string, skewSeconds = 30): boolean {
  const exp = getJwtExp(token);
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return now >= exp - skewSeconds;
}

// fila de refresh
let refreshing = false;
let queue: Array<{
  res: (v: any) => void;
  rej: (e: any) => void;
  original: any;
}> = [];

export const licenseApi = axios.create({ baseURL: BASE_URL });

// injeta Authorization e tenta renovar se expirado
licenseApi.interceptors.request.use(async (cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (isExpired(token) && creds) {
    try {
      await login(creds);
    } catch {
      /* deixa cair no 401 */
    }
  }
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    cfg.headers = cfg.headers || {};
    (cfg.headers as any).Authorization = `Bearer ${t}`;
  }
  return cfg;
});

// retry em 401 com relogin usando credenciais em memória
licenseApi.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original: AxiosRequestConfig & { _retry?: boolean } =
      err.config || {};

    creds = {
      email: process.env.REACT_APP_LICENSE_EMAIL,
      senha: process.env.REACT_APP_PASSWORD,
    };

    if (err.response?.status === 401 && !original._retry && creds) {
      original._retry = true;

      if (refreshing) {
        return new Promise((res, rej) => queue.push({ res, rej, original }));
      }

      try {
        refreshing = true;
        await login(creds);
        original.headers = original.headers || {};
        (
          original.headers as any
        ).Authorization = `Bearer ${localStorage.getItem(TOKEN_KEY)}`;
        const resp = await licenseApi.request(original);
        queue.forEach(({ res }) => res(licenseApi.request(original)));
        queue = [];
        return resp;
      } catch (e) {
        queue.forEach(({ rej }) => rej(e));
        queue = [];
        throw e;
      } finally {
        refreshing = false;
      }
    }
    throw err;
  },
);

// ---- endpoints
type LoginInput = { email: string; senha: string };
type LoginResponse = {
  token?: string;
  access_token?: string;
  [k: string]: any;
};

interface IUpdateLicense {
  codigo: string;
  status: string;
}
export async function login({
  email,
  senha,
}: LoginInput): Promise<LoginResponse> {
  const { data } = await axios.post<LoginResponse>(
    `${BASE_URL}/login`,
    { email, senha },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const tok = data.token || data.access_token;
  if (!tok) throw new Error('Token não retornado');
  localStorage.setItem(TOKEN_KEY, tok); // só token
  return data;
}

export function getKey(chave: string | number) {
  return licenseApi.get('/buscar-chave', { params: { chave } });
}

export function getLicense(codigo: string) {
  return licenseApi.get('/licencas', { params: { codigo } });
}

export function updatekey(data: Record<string, any>) {
  return licenseApi.put('/atualizar-status-chave', data);
}

export function updateLicense(data: IUpdateLicense) {
  return licenseApi.put('/atualizar-licenca', data);
}
